import React, { useCallback } from 'react'
import { BaseDragHandle } from '@fs/zion-ui/quarks-authorized-use-only'
import { colors } from '@fs/zion-ui'
import { css } from '@emotion/core'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { getRotationMatrix, rotatePointWithMatrix } from './zoomPanDraggableUtils'

const HANDLE_SIZE = 24
const CONTAINER_MIN_SIZE = 24

const baseDragHandleCss = css`
  block-size: ${HANDLE_SIZE}px;
  border-radius: 4px;
  inline-size: ${HANDLE_SIZE}px;
  outline: none;
  position: absolute;

  ::after {
    background: ${colors.gray00};
    block-size: 100%;
    border: 2px solid ${colors.blue40};
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    content: '';
    display: block;
    inline-size: 100%;
  }

  &:focus-visible {
    outline: 4px solid ${colors.blue40};
  }
`

function getRotatedResizeCursor(rotAngle, isBottomRight, isBottomLeft, isTopRight, isTopLeft) {
  let baseAngle = 0
  if (isBottomRight) baseAngle = 45
  else if (isTopLeft) baseAngle = 225
  else if (isBottomLeft) baseAngle = 135
  else if (isTopRight) baseAngle = 315

  const angle = (baseAngle + rotAngle) % 360

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
      <g transform="rotate(${angle},12,12)">
        <polyline
          points="1,12 4,9 4,12 20,12 20,9 23,12 20,15 20,12 4,12 4,15 1,12"
          fill="black"
          stroke="white"
          stroke-width="4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <polyline
          points="1,12 4,9 4,12 20,12 20,9 23,12 20,15 20,12 4,12 4,15 1,12"
          fill="black"
          stroke="black"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>
    </svg>
    `
}

export function FreeformCornerResizeHandles({ testIdPrefix, getBounds, maxSize, minSize, ...restProps }) {
  const sharedProps = {
    getBounds,
    maxSize,
    minSize,
    ...restProps,
  }

  return (
    <>
      <FreeformCornerResizeHandle
        {...sharedProps}
        bottomRight
        data-testid={testIdPrefix ? `${testIdPrefix}-bottom-right` : undefined}
      />
      <FreeformCornerResizeHandle
        {...sharedProps}
        bottomLeft
        data-testid={testIdPrefix ? `${testIdPrefix}-bottom-left` : undefined}
      />
      <FreeformCornerResizeHandle
        {...sharedProps}
        data-testid={testIdPrefix ? `${testIdPrefix}-top-right` : undefined}
        topRight
      />
      <FreeformCornerResizeHandle
        {...sharedProps}
        data-testid={testIdPrefix ? `${testIdPrefix}-top-left` : undefined}
        topLeft
      />
    </>
  )
}

export default function FreeformCornerResizeHandle({
  bottomLeft,
  bottomRight,
  getBounds: containerGetBounds,
  maxSize,
  minSize = CONTAINER_MIN_SIZE,
  topLeft,
  topRight,
  rotation = 0,
  ...props
}) {
  const [t] = useTranslation()

  if (!bottomLeft && !bottomRight && !topLeft && !topRight) {
    bottomRight = true
  }

  const calculateNewDimensions = useCallback(
    ({ currentRect, delta, handleType }) => {
      const { x, y, width, height } = currentRect
      let newWidth = width
      let newHeight = height
      let newX = x
      let newY = y

      if (handleType === 'bottomRight') {
        newWidth = Math.max(minSize, width + delta.x)
        newHeight = Math.max(minSize, height + delta.y)
      } else if (handleType === 'bottomLeft') {
        newWidth = Math.max(minSize, width - delta.x)
        newHeight = Math.max(minSize, height + delta.y)
        newX = x + width - newWidth
      } else if (handleType === 'topRight') {
        newWidth = Math.max(minSize, width + delta.x)
        newHeight = Math.max(minSize, height - delta.y)
        newY = y + height - newHeight
      } else if (handleType === 'topLeft') {
        newWidth = Math.max(minSize, width - delta.x)
        newHeight = Math.max(minSize, height - delta.y)
        newX = x + width - newWidth
        newY = y + height - newHeight
      }

      // Apply max size constraints
      if (maxSize) {
        if (newWidth > maxSize) {
          if (handleType === 'bottomLeft' || handleType === 'topLeft') {
            newX = x + width - maxSize
          }
          newWidth = maxSize
        }
        if (newHeight > maxSize) {
          if (handleType === 'topLeft' || handleType === 'topRight') {
            newY = y + height - maxSize
          }
          newHeight = maxSize
        }
      }

      return { newWidth, newHeight, newX, newY }
    },
    [minSize, maxSize]
  )

  const applyBoundaryConstraints = useCallback(
    ({ boundary, dimensions }) => {
      if (!boundary) return dimensions

      let { newWidth, newHeight, newX, newY } = dimensions
      const boundaryRight = boundary.x + boundary.width
      const boundaryBottom = boundary.y + boundary.height

      // Constrain to fit within boundary
      if (newX < boundary.x) {
        const diff = boundary.x - newX
        newX = boundary.x
        newWidth = Math.max(minSize, newWidth - diff)
      }
      if (newY < boundary.y) {
        const diff = boundary.y - newY
        newY = boundary.y
        newHeight = Math.max(minSize, newHeight - diff)
      }
      if (newX + newWidth > boundaryRight) {
        newWidth = Math.max(minSize, boundaryRight - newX)
      }
      if (newY + newHeight > boundaryBottom) {
        newHeight = Math.max(minSize, boundaryBottom - newY)
      }

      return { newWidth, newHeight, newX, newY }
    },
    [minSize]
  )

  const getHandleType = useCallback(() => {
    if (bottomRight) return 'bottomRight'
    if (bottomLeft) return 'bottomLeft'
    if (topRight) return 'topRight'
    if (topLeft) return 'topLeft'
    return 'bottomRight'
  }, [bottomRight, bottomLeft, topRight, topLeft])

  const getHandleLabel = useCallback(() => {
    if (bottomRight)
      return t('FreeformCornerResizeHandle.keyboardDescription.bottomRight', 'Bottom Right Resize Handle')
    if (bottomLeft) return t('FreeformCornerResizeHandle.keyboardDescription.bottomLeft', 'Bottom Left Resize Handle')
    if (topRight) return t('FreeformCornerResizeHandle.keyboardDescription.topRight', 'Top Right Resize Handle')
    if (topLeft) return t('FreeformCornerResizeHandle.keyboardDescription.topLeft', 'Top Left Resize Handle')
    return t('FreeformCornerResizeHandle.keyboardDescription.bottomRight', 'Bottom Right Resize Handle')
  }, [bottomRight, t, bottomLeft, topRight, topLeft])

  const handleResize = useCallback(
    ({ currentRect, delta }) => {
      const { x, y, width, height } = currentRect
      const rotationMatrix = getRotationMatrix(rotation)
      const inverseRotationMatrix = getRotationMatrix(-rotation)

      // Transform delta into local element axes
      const localDelta = rotatePointWithMatrix(delta, inverseRotationMatrix)

      // Identify anchor (opposite) corner in local space
      let anchorLocal = { x: 0, y: 0 }
      if (bottomRight) {
        anchorLocal = { x, y }
      } else if (bottomLeft) {
        anchorLocal = { x: x + width, y }
      } else if (topRight) {
        anchorLocal = { x, y: y + height }
      } else if (topLeft) {
        anchorLocal = { x: x + width, y: y + height }
      }

      // Transform anchor to global space
      const cx = x + width / 2
      const cy = y + height / 2
      const anchorVec = { x: anchorLocal.x - cx, y: anchorLocal.y - cy }
      const rotatedAnchorVec = rotatePointWithMatrix(anchorVec, rotationMatrix)
      const anchorGlobal = {
        x: cx + rotatedAnchorVec.x,
        y: cy + rotatedAnchorVec.y,
      }

      // Get boundary
      const containerBounds = containerGetBounds()
      const containerBoundary = containerBounds?.boundary

      // Calculate new dimensions using localDelta
      const handleType = getHandleType()
      let dimensions = calculateNewDimensions({ currentRect, delta: localDelta, handleType })

      // Apply boundary constraints
      dimensions = applyBoundaryConstraints({ boundary: containerBoundary, dimensions })

      // Compute anchor offset in new local space
      let newAnchorOffset = { x: 0, y: 0 }
      if (bottomRight) {
        newAnchorOffset = { x: -dimensions.newWidth / 2, y: -dimensions.newHeight / 2 }
      } else if (bottomLeft) {
        newAnchorOffset = { x: dimensions.newWidth / 2, y: -dimensions.newHeight / 2 }
      } else if (topRight) {
        newAnchorOffset = { x: -dimensions.newWidth / 2, y: dimensions.newHeight / 2 }
      } else if (topLeft) {
        newAnchorOffset = { x: dimensions.newWidth / 2, y: dimensions.newHeight / 2 }
      }

      // Rotate new anchor offset by rotation
      const newAnchorOffsetRot = rotatePointWithMatrix(newAnchorOffset, rotationMatrix)

      // Set new center so anchor stays at anchorGlobal
      const finalCx = anchorGlobal.x - newAnchorOffsetRot.x
      const finalCy = anchorGlobal.y - newAnchorOffsetRot.y
      const finalX = finalCx - dimensions.newWidth / 2
      const finalY = finalCy - dimensions.newHeight / 2

      return {
        height: dimensions.newHeight,
        width: dimensions.newWidth,
        x: finalX,
        y: finalY,
      }
    },
    [
      bottomLeft,
      bottomRight,
      topLeft,
      topRight,
      containerGetBounds,
      calculateNewDimensions,
      applyBoundaryConstraints,
      getHandleType,
      rotation,
    ]
  )

  const getBounds = useCallback(() => {
    return {
      min: { x: -Infinity, y: -Infinity },
      max: { x: Infinity, y: Infinity },
      minWidth: 0,
      minHeight: 0,
    }
  }, [])

  return (
    <BaseDragHandle
      {...props}
      css={baseDragHandleCss}
      cursor={`url("data:image/svg+xml;utf8,${encodeURIComponent(
        getRotatedResizeCursor(rotation, !!bottomRight, !!bottomLeft, !!topRight, !!topLeft)
      )}") 12 12, auto`}
      getBounds={getBounds}
      keyboardDescription={getHandleLabel()}
      onDragMove={(delta, currentRect) => handleResize({ currentRect, delta })}
      style={{
        ...(bottomLeft && { bottom: HANDLE_SIZE / -2, left: HANDLE_SIZE / -2 }),
        ...(bottomRight && { bottom: HANDLE_SIZE / -2, right: HANDLE_SIZE / -2 }),
        ...(topLeft && { top: HANDLE_SIZE / -2, left: HANDLE_SIZE / -2 }),
        ...(topRight && { top: HANDLE_SIZE / -2, right: HANDLE_SIZE / -2 }),
        ...props.style,
      }}
      tabIndex={0}
      role="slider"
      data-move-handle
    />
  )
}

FreeformCornerResizeHandle.propTypes = {
  bottomLeft: PropTypes.bool,
  bottomRight: PropTypes.bool,
  getBounds: PropTypes.func,
  maxSize: PropTypes.number,
  minSize: PropTypes.number,
  topLeft: PropTypes.bool,
  topRight: PropTypes.bool,
  rotation: PropTypes.number,
}
